<script setup lang="ts">
import { computed } from 'vue'

import {
  BlockContainer,
  RelatedArticlesTeaser,
  RelatedArticlesTeaserContent,
  RelatedArticlesTeaserHeadline
} from '@toni/brand-ui'
import { usePageStore } from '@toni/rendering/src/stores/page'

import { useDisallowAds } from '../../../../advertisement/composables/use-disallow-ads'
import { usePageBlockWidth } from '../../../../composables/use-page-block-width'
import { useContentBlock } from '../../../../content-blocks/composables/use-content-block'
import NativeTeaserItem from '../../../ui/teaser/NativeTeaserItem.vue'
import { isNativeTeaserItem } from '../../../ui/teaser/teaser-group-constants'
import { useNativeTeasers } from '../../teaser/use-native-teasers'
import RelatedArticlesItem from './RelatedArticlesItem.vue'

const props = defineProps<{
    id: string
  }>()

const pageStore = usePageStore()
const brand = computed(() => pageStore.brandFromStore as string)

const isHome = computed(() => pageStore.isHome as boolean)
const size = usePageBlockWidth(pageStore.pageData?.pageMeta, isHome.value, brand.value)

const id = computed(() => props.id)
const contentBlock = useContentBlock(id.value, '__injected_related__')

const asideTeasers = computed(() => {
  const currentIndex = contentBlock.value.data.relatedArticlesMarker.injectedIndex ?? 0

  if (!pageStore.pageData.metaData?.asideTeasers) {
    return []
  }

  // Calculate how many teasers to show per block (e.g., 4)
  const teasersPerBlock = 3

  // Calculate the start and end indices for this specific block
  const startIndex = currentIndex * teasersPerBlock
  const endIndex = Math.min(startIndex + teasersPerBlock, pageStore.pageData.metaData.asideTeasers.length)

  // Slice the array to get only the teasers for this specific block
  return pageStore.pageData.metaData.asideTeasers.slice(startIndex, endIndex)
})

const nativeTeaserOptions = computed(() => ({
  currentContentBlockId: id.value,
  teasers: asideTeasers.value
}))

const disallowAds = useDisallowAds()

const enabled = computed(() => {
  return !disallowAds.value.disallowNativeTeasers && asideTeasers.value.length === 3
})

const teasersWithNativeTeasers = useNativeTeasers(nativeTeaserOptions.value)
</script>

<template>
  <BlockContainer v-if="enabled"
                  :size="size">
    <RelatedArticlesTeaser>
      <RelatedArticlesTeaserHeadline>
        <slot name="headline" />
      </RelatedArticlesTeaserHeadline>
      <RelatedArticlesTeaserContent>
        <template v-for="item in teasersWithNativeTeasers">
          <NativeTeaserItem v-if="isNativeTeaserItem(item)"
                            :key="`native-teaser-${item.fallback.assetId}`"
                            :native-teaser-index="item.nativeTeaserIndex">
            <RelatedArticlesItem :item="item.fallback" />
          </NativeTeaserItem>
          <RelatedArticlesItem v-else
                               :key="item.assetId"
                               :item="item" />
        </template>
      </RelatedArticlesTeaserContent>
    </RelatedArticlesTeaser>
  </BlockContainer>
</template>
